
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { I18nService } from '@/services/i18n-service';
import { Message } from '@patsnap/tahiti-core';
import type { RecommendItem, CreateProjectWithItemsParams, GroupedRecommendItem, HistoryResponse } from '@/domains/project/types';
import { SuggestApi } from '../apis/suggest-api';
import { RouterService } from '@eureka/shared/core-services/src/vue-router-service';

export interface SuggestService {
    recommendItems: RecommendItem[]
    groupedRecommendItems: GroupedRecommendItem[]
    selectedItems: Set<string>
    creating: boolean
    getSuggestLoading: boolean
    hasRecordHistory: boolean
    getHistoryLoading: boolean
    createProjectWithItems(params: CreateProjectWithItemsParams): Promise<string>
    getRecommendItems(): Promise<void>
    getHistoryItems(): Promise<HistoryResponse>
    updateSelectedItems(items: Set<string>): void
}

export const SuggestService = Symbol('SuggestService');

@injectable()
export class SuggestServiceImpl implements SuggestService {
    private _recommendItems: RecommendItem[] = []
    private _groupedRecommendItems: GroupedRecommendItem[] = []
    private _creating = false
    private _getSuggestLoading = false
    private _getHistoryLoading = false
    private _selectedItems: Set<string> = new Set()
    private _hasRecordHistory = false

    constructor(
        @inject(SuggestApi) private readonly _suggestApi: SuggestApi,
        @inject(I18nService) private readonly _i18nService: I18nService,
        @inject(Message) private readonly _message: Message,
        @inject(RouterService) private readonly _routerService: RouterService
    ) {}

    get creating() {
        return this._creating;
    }

    get getHistoryLoading() {
        return this._getHistoryLoading;
    }

    get getSuggestLoading() {
        return this._getSuggestLoading;
    }

    get selectedItems() {
        return this._selectedItems;
    }

    get recommendItems() {
        return this._recommendItems;
    }

    get groupedRecommendItems() {
        return this._groupedRecommendItems;
    }

    get hasRecordHistory() {
        return this._hasRecordHistory;
    }

    updateSelectedItems(items: Set<string>) {
        this._selectedItems = items;
    }

    getHistoryItems(): Promise<HistoryResponse> {
        this._getHistoryLoading = true
        return this._suggestApi.getHistoryItems().then((response) => {
            this._recommendItems = response.recommend_items
            this._hasRecordHistory = response.has_record_history
            return response
        }).finally(() => {
            this._getHistoryLoading = false
        })
    }

    getRecommendItems(): Promise<void> {
        this._getSuggestLoading = true
        return this._suggestApi.getClusteringItems().then((response) => {
            this._groupedRecommendItems = response
        }).finally(() => {
            this._getSuggestLoading = false
        })
    }

    createProjectWithItems(params: CreateProjectWithItemsParams): Promise<string> {
        this._creating = true
        return this._suggestApi.createProjectWithItems(params).then((projectId) => {
            this._message.success({
                message: this._i18nService.t('workProject.createSuccess'),
            });
            this._routerService.push({
                name: 'GraphDetail',
                params: {
                    graph_id: projectId,
                },
            });
            return projectId;
        }).finally(() => {
            this._creating = false
        });
    }
}
